// distancing-cowork 의 /chat/threads/... endpoint wrapper. SSE 와 같은 호스트 다른 path.
// REACT_APP_AGENT_API_HOST 미설정 시 in-memory mock (개발 fallback).
//
// CONTRACT 는 BE `docs/api/chat-history.md` 와 1:1 — DTO 필드명·status code 동기.

const HOST = process.env.REACT_APP_AGENT_API_HOST || "";

export interface ChatThreadDTO {
  thread_id: string;
  task_id: string;
  created_at: string;
  updated_at: string;
  title: string | null;
  last_user_message: string | null;
  last_assistant_preview: string | null;
  is_cleared: boolean;
}

export interface ChatTurnDTO {
  thread_id: string;
  turn_id: string;
  sequence: number;
  role: "user" | "assistant";
  payload: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface GetThreadResponse {
  thread: ChatThreadDTO;
  turns: ChatTurnDTO[];
}

export interface ListThreadsResponse {
  threads: ChatThreadDTO[];
}

export interface UpsertThreadBody {
  task_id: string;
  title?: string | null;
  last_user_message?: string | null;
  last_assistant_preview?: string | null;
  is_cleared?: boolean | null;
}

export interface UpsertTurnBody {
  sequence: number;
  role: "user" | "assistant";
  payload: Record<string, unknown>;
}

// =========================
// HTTP helpers (실서버)
// =========================

async function request<T>(
  method: "GET" | "PUT" | "DELETE",
  path: string,
  body?: unknown
): Promise<{ status: number; data: T | null }> {
  const res = await fetch(`${HOST}${path}`, {
    method,
    headers: body !== undefined ? { "Content-Type": "application/json" } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 404) return { status: 404, data: null };
  if (!res.ok) {
    throw new Error(`chat history API ${method} ${path} failed: ${res.status}`);
  }
  if (res.status === 204) return { status: 204, data: null };
  const data = (await res.json()) as T;
  return { status: res.status, data };
}

// =========================
// In-memory mock — REACT_APP_AGENT_API_HOST 미설정 시 사용
// =========================

const mockThreads = new Map<string, ChatThreadDTO>();
const mockTurns = new Map<string, ChatTurnDTO>();

function mockTurnKey(threadId: string, turnId: string): string {
  return `${threadId}${turnId}`;
}

function mockNow(): string {
  return new Date().toISOString();
}

function mockListThreads(
  taskId: string,
  includeCleared: boolean,
  limit: number
): ChatThreadDTO[] {
  const rows = Array.from(mockThreads.values()).filter(
    (t) => t.task_id === taskId && (includeCleared || !t.is_cleared)
  );
  rows.sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1));
  return rows.slice(0, limit);
}

function mockGetThread(threadId: string): GetThreadResponse | null {
  const thread = mockThreads.get(threadId);
  if (!thread) return null;
  const turns: ChatTurnDTO[] = [];
  mockTurns.forEach((t) => {
    if (t.thread_id === threadId) turns.push(t);
  });
  turns.sort((a, b) => a.sequence - b.sequence);
  return { thread, turns };
}

function mockUpsertThread(
  threadId: string,
  body: UpsertThreadBody
): ChatThreadDTO {
  const existing = mockThreads.get(threadId);
  const now = mockNow();
  if (!existing) {
    const created: ChatThreadDTO = {
      thread_id: threadId,
      task_id: body.task_id,
      created_at: now,
      updated_at: now,
      title: body.title ?? null,
      last_user_message: body.last_user_message ?? null,
      last_assistant_preview: body.last_assistant_preview ?? null,
      is_cleared: body.is_cleared ?? false,
    };
    mockThreads.set(threadId, created);
    return created;
  }
  const merged: ChatThreadDTO = {
    ...existing,
    updated_at: now,
    title: body.title ?? existing.title,
    last_user_message: body.last_user_message ?? existing.last_user_message,
    last_assistant_preview:
      body.last_assistant_preview ?? existing.last_assistant_preview,
    is_cleared: body.is_cleared ?? existing.is_cleared,
  };
  mockThreads.set(threadId, merged);
  return merged;
}

function mockUpsertTurn(
  threadId: string,
  turnId: string,
  body: UpsertTurnBody
): ChatTurnDTO | null {
  if (!mockThreads.has(threadId)) return null;
  const key = mockTurnKey(threadId, turnId);
  const existing = mockTurns.get(key);
  const now = mockNow();
  const turn: ChatTurnDTO = {
    thread_id: threadId,
    turn_id: turnId,
    sequence: body.sequence,
    role: body.role,
    payload: body.payload,
    created_at: existing ? existing.created_at : now,
    updated_at: now,
  };
  mockTurns.set(key, turn);
  // thread.updated_at bump — list 정렬 신선도.
  const thread = mockThreads.get(threadId);
  if (thread) mockThreads.set(threadId, { ...thread, updated_at: now });
  return turn;
}

function mockClearThread(threadId: string): boolean {
  const t = mockThreads.get(threadId);
  if (!t) return false;
  mockThreads.set(threadId, { ...t, is_cleared: true, updated_at: mockNow() });
  return true;
}

// =========================
// Public API
// =========================

export async function listThreads(
  taskId: string,
  opts: { includeCleared?: boolean; limit?: number } = {}
): Promise<ChatThreadDTO[]> {
  const includeCleared = opts.includeCleared ?? false;
  const limit = opts.limit ?? 10;
  if (!HOST) return mockListThreads(taskId, includeCleared, limit);
  const params = new URLSearchParams({
    task_id: taskId,
    include_cleared: String(includeCleared),
    limit: String(limit),
  });
  const { data } = await request<ListThreadsResponse>(
    "GET",
    `/chat/threads?${params}`
  );
  return data?.threads ?? [];
}

export async function getThread(
  threadId: string
): Promise<GetThreadResponse | null> {
  if (!HOST) return mockGetThread(threadId);
  const { data } = await request<GetThreadResponse>(
    "GET",
    `/chat/threads/${encodeURIComponent(threadId)}`
  );
  return data;
}

export async function upsertThread(
  threadId: string,
  body: UpsertThreadBody
): Promise<ChatThreadDTO> {
  if (!HOST) return mockUpsertThread(threadId, body);
  const { data } = await request<ChatThreadDTO>(
    "PUT",
    `/chat/threads/${encodeURIComponent(threadId)}`,
    body
  );
  if (!data) {
    throw new Error("upsertThread: unexpected empty response");
  }
  return data;
}

export async function upsertTurn(
  threadId: string,
  turnId: string,
  body: UpsertTurnBody
): Promise<ChatTurnDTO | null> {
  if (!HOST) return mockUpsertTurn(threadId, turnId, body);
  const res = await fetch(
    `${HOST}/chat/threads/${encodeURIComponent(threadId)}/turns/${encodeURIComponent(turnId)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  if (res.status === 404) return null; // thread missing — caller 가 thread upsert 먼저 안 한 케이스
  if (!res.ok) {
    throw new Error(`upsertTurn failed: ${res.status}`);
  }
  return (await res.json()) as ChatTurnDTO;
}

export async function clearThread(threadId: string): Promise<boolean> {
  if (!HOST) return mockClearThread(threadId);
  const { status } = await request<{ is_cleared: boolean }>(
    "DELETE",
    `/chat/threads/${encodeURIComponent(threadId)}`
  );
  return status !== 404;
}

// =========================
// 테스트용 — mock state 리셋
// =========================

export function __resetMockForTests(): void {
  mockThreads.clear();
  mockTurns.clear();
}
